'use client';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { ButtonProps } from '@osmosis/tailwind-ui/components/Button';
import React, { useState } from 'react';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import { FormattedMessage, useIntl } from 'react-intl';
import { NotesSection } from '@osmosis/shared/types/learndb/learnData';
import { useLearnAccess } from '../../../../context/LearnAccessContext';
import dynamic from 'next/dynamic';
import ExpiredUserLockoutModal from '../../ExpiredUserModal';
import { ExpiredUserModalType } from '../../../../constants/ExpiredUserModalConfig';
import { useUserContext } from '../../../../context/UserContext';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { downloadNotes } from '../../../../lib/notes/download';
import { CommonLearnBlockProps } from '../LearnBlock';

type Props = {
  notes: NotesSection;
} & CommonLearnBlockProps;

const LearnBlock = dynamic(() => import('../LearnBlock'), { ssr: false });
const NotesBlockView = dynamic(() => import('./NotesBlockView'), {
  ssr: false,
});

/**
 * NotesBlock component displays the notes section with download functionality.
 */
export default function NotesBlock(props: Props): JSX.Element {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const { notes, contentOnly } = props;
  const { pages, notesLink } = notes;

  const { hasAccess } = useLearnAccess();
  const { user } = useUserContext();

  const content = <NotesBlockView notes={notes} />;

  const intl = useIntl();

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadNotes(pages, notesLink);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadClick = () => {
    if (hasAccess) {
      handleDownload();
    } else {
      // Show expired modal for users without access
      setShowExpiredModal(true);
    }
  };

  const buttonProps = {
    sx: { display: ['block', 'none'] },
    variant: 'outline',
    size: 's',
    color: 'primary',
    isLoading: isDownloading,
    disabled: isDownloading,
    onClick: handleDownloadClick,
    startIcon: <Icon icon={faDownload} />,
    className: 'md:flex dark:text-white',
  };

  const controls = (
    <>
      <Flex className={`items-center`}>
        {user && (
          <Button
            {...(buttonProps as ButtonProps)}
            data-testid={'download-button'}
          >
            <FormattedMessage
              id="learn.notes_section.download.text"
              defaultMessage="Download"
            />
          </Button>
        )}
        <P className={`ml-4 mr-2`}>
          {pages.length}&nbsp;
          <FormattedMessage
            id="learn.notes_section.print.pages"
            defaultMessage="pages"
          />
        </P>
      </Flex>

      {showExpiredModal && (
        <ExpiredUserLockoutModal
          modalType={ExpiredUserModalType.DOWNLOAD_NOTES}
          isVisible={showExpiredModal}
          hide={() => setShowExpiredModal(false)}
        />
      )}
    </>
  );

  return (
    <LearnBlock
      title={intl.formatMessage({
        id: 'learn.notes_section.title',
        defaultMessage: 'High Yield Notes',
      })}
      icon={faFileAlt}
      content={content}
      controls={controls}
      languageTag={true}
      contentOnly={contentOnly}
    />
  );
}
